import { useState, type FC } from 'react';

import cx from 'classnames';

import { GAME_SOUND_MUTED_KEY, type IGameLayoutProps } from '../../types';
import { PlayerCard } from '../PlayerCard';
import { RulesModal } from '../RulesModal';
import { SurrenderDialog } from '../SurrenderDialog';

import styles from './GameLayout.module.scss';

function IconSoundLoud({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <title>Sound on</title>
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
    </svg>
  );
}

function IconSoundOff({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <title>Sound off</title>
      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
    </svg>
  );
}

function IconHelpOutline({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <title>How to play</title>
      <path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z" />
    </svg>
  );
}

function getInitialMuted(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  return window.localStorage.getItem(GAME_SOUND_MUTED_KEY) === 'true';
}

export const GameLayout: FC<IGameLayoutProps> = ({
  activePlayerId,
  disableHorizontalPadding = false,
  fullWidthMobilePlayersPreset = 'default',
  gameColumnClassName,
  gameContent,
  gameName = '',
  labels,
  layoutVariant = 'default',
  onSurrender,
  player1,
  player1TimeLeftMs,
  player2,
  player2TimeLeftMs,
  rulesContent = '',
  turnDurationMs,
}) => {
  void turnDurationMs;
  const [showSurrenderDialog, setShowSurrenderDialog] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [isMuted, setIsMuted] = useState(getInitialMuted);

  const isPlayer1Active = activePlayerId === player1.id;
  const isPlayer2Active = activePlayerId === player2.id;

  const handleSurrenderClick = () => {
    setShowSurrenderDialog(true);
  };

  const handleKeepPlaying = () => {
    setShowSurrenderDialog(false);
  };

  const handleConfirmSurrender = () => {
    setShowSurrenderDialog(false);
    if (onSurrender) {
      onSurrender();
    }
  };

  const handleMuteToggle = () => {
    const next = !isMuted;
    setIsMuted(next);
    try {
      window.localStorage.setItem(
        GAME_SOUND_MUTED_KEY,
        next ? 'true' : 'false',
      );
    } catch {
      // ignore
    }
  };

  const handleCloseRules = () => setShowRulesModal(false);
  const handleGotIt = () => setShowRulesModal(false);

  return (
    <div
      className={cx(
        styles.gameLayout,
        disableHorizontalPadding && styles.horizontalPaddingDisabled,
      )}
    >
      <header className={styles.header}>
        <div />
        <div className={styles.headerButtons}>
          <button
            type="button"
            className={styles.headerButton}
            onClick={handleMuteToggle}
            aria-pressed={isMuted}
            aria-label={isMuted ? labels.unmute : labels.mute}
          >
            {isMuted ? (
              <IconSoundOff className={styles.headerButtonIcon} />
            ) : (
              <IconSoundLoud className={styles.headerButtonIcon} />
            )}
            <span>{isMuted ? labels.unmute : labels.mute}</span>
          </button>
          <button
            type="button"
            className={styles.headerButton}
            onClick={() => setShowRulesModal(true)}
            disabled={!gameName}
            aria-label={labels.howToPlay}
          >
            <IconHelpOutline className={styles.headerButtonIcon} />
            <span className={styles.headerButtonLabelMobileHidden}>
              {labels.howToPlay}
            </span>
          </button>
        </div>
      </header>
      {(layoutVariant !== 'fullWidth' ||
        fullWidthMobilePlayersPreset === 'compactRow') && (
        <div className={styles.playersRow}>
          <PlayerCard
            displayMode={
              fullWidthMobilePlayersPreset === 'compactRow'
                ? 'indicatorNameTimer'
                : 'default'
            }
            isActive={isPlayer1Active}
            player={player1}
            size="small"
            statusIndicatorVariant={
              fullWidthMobilePlayersPreset === 'compactRow'
                ? 'primary'
                : 'active'
            }
            timeLeftMs={player1TimeLeftMs}
          />
          <PlayerCard
            displayMode={
              fullWidthMobilePlayersPreset === 'compactRow'
                ? 'indicatorNameTimer'
                : 'default'
            }
            isActive={isPlayer2Active}
            player={player2}
            size="small"
            statusIndicatorVariant={
              fullWidthMobilePlayersPreset === 'compactRow'
                ? 'accent'
                : 'active'
            }
            timeLeftMs={player2TimeLeftMs}
          />
        </div>
      )}

      {layoutVariant === 'fullWidth' ? (
        <>
          <div
            className={cx(
              styles.fullWidthPlayersRow,
              fullWidthMobilePlayersPreset === 'compactRow' &&
                styles.fullWidthPlayersRowHiddenMobile,
            )}
          >
            <PlayerCard
              isActive={isPlayer1Active}
              player={player1}
              size="desktopBlock"
              statusIndicatorVariant={
                fullWidthMobilePlayersPreset === 'compactRow'
                  ? 'primary'
                  : 'active'
              }
              timeLeftMs={player1TimeLeftMs}
            />
            <PlayerCard
              isActive={isPlayer2Active}
              player={player2}
              size="desktopBlock"
              statusIndicatorVariant={
                fullWidthMobilePlayersPreset === 'compactRow'
                  ? 'accent'
                  : 'active'
              }
              timeLeftMs={player2TimeLeftMs}
            />
          </div>
          <div className={cx(styles.gameColumnFullWidth, gameColumnClassName)}>
            {gameContent}
          </div>
        </>
      ) : (
        <div className={styles.content}>
          <div className={styles.playerColumn}>
            <PlayerCard
              isActive={isPlayer1Active}
              player={player1}
              timeLeftMs={player1TimeLeftMs}
            />
          </div>

          <div className={cx(styles.gameColumn, gameColumnClassName)}>
            {gameContent}
          </div>

          <div className={styles.playerColumn}>
            <PlayerCard
              isActive={isPlayer2Active}
              player={player2}
              timeLeftMs={player2TimeLeftMs}
            />
          </div>

          <div className={styles.playersColumn}>
            <PlayerCard
              isActive={isPlayer1Active}
              player={player1}
              size="medium"
              timeLeftMs={player1TimeLeftMs}
            />
            <PlayerCard
              isActive={isPlayer2Active}
              player={player2}
              size="medium"
              timeLeftMs={player2TimeLeftMs}
            />
          </div>
        </div>
      )}

      {onSurrender && (
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.surrenderButton}
            onClick={handleSurrenderClick}
          >
            {labels.giveUp}
          </button>
        </div>
      )}

      <SurrenderDialog
        isOpen={showSurrenderDialog}
        labels={labels.surrenderDialog}
        onKeepPlaying={handleKeepPlaying}
        onSurrender={handleConfirmSurrender}
      />

      {gameName && (
        <RulesModal
          isOpen={showRulesModal}
          gotItLabel={labels.gotIt}
          rulesContent={rulesContent}
          rulesTitle={labels.rulesTitle(gameName)}
          onClose={handleCloseRules}
          onGotIt={handleGotIt}
        />
      )}
    </div>
  );
};
